import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Lock, Check } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { NavBar } from '../components/NavBar';
import { ClubBuilderBasicsPanel } from '../components/ClubBuilderBasicsPanel';
import { PackSizesPanel } from '../components/PackSizesPanel';
import type { PackSize } from '../components/PackSizesPanel';
import { MembershipPanel } from '../components/MembershipPanel';
import { FlowPanel } from '../components/FlowPanel';
import { ThemingPanel } from '../components/ThemingPanel';
import { SellingPlansPanel } from '../components/SellingPlansPanel';
const TABS = [{
  id: 'tab-basics',
  label: 'Basics'
}, {
  id: 'tab-pack-sizes',
  label: 'Pack Sizes',
  count: 0
}, {
  id: 'tab-membership',
  label: 'Membership'
}, {
  id: 'tab-flow',
  label: 'Flow'
}, {
  id: 'tab-theming',
  label: 'Theming'
}, {
  id: 'tab-selling-plans',
  label: 'Selling Plans',
  count: 0
}] as const;
// Tab index constants
const TAB_INDEX: Record<string, number> = {
  'tab-basics': 0,
  'tab-pack-sizes': 1,
  'tab-membership': 2,
  'tab-flow': 3,
  'tab-theming': 4,
  'tab-selling-plans': 5
};
export function BoxBuilderPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('tab-basics');
  const [completedUpTo, setCompletedUpTo] = useState<number>(-1);
  const [boxType, setBoxType] = useState<'custom' | 'curated'>('custom');
  const [packSizes, setPackSizes] = useState<PackSize[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };
  // Central advance function: marks tab at `index` as completed and moves to next tab
  const advanceFrom = (index: number) => {
    setCompletedUpTo(prev => Math.max(prev, index));
    const nextTab = TABS[index + 1];
    if (nextTab) {
      setActiveTab(nextTab.id);
    }
  };
  // Ref for scroll anchor at the top of the page
  const topRef = useRef<HTMLDivElement>(null);
  // Scroll to top whenever the active tab changes
  useEffect(() => {
    // Use multiple strategies to ensure scroll reaches the top in any environment (iframe, etc.)
    const timer = setTimeout(() => {
      if (topRef.current) {
        topRef.current.scrollIntoView({
          block: 'start',
          behavior: 'instant' as ScrollBehavior
        });
      }
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
    return () => clearTimeout(timer);
  }, [activeTab]);
  const previewPublishEnabled = completedUpTo >= 4;
  const tabsWithCounts = TABS.map(tab => {
    if (tab.id === 'tab-pack-sizes') return {
      ...tab,
      count: packSizes.length
    };
    return tab;
  });
  return <div className="bg-[#F1F5F9] min-h-screen p-6" data-id="element-4820">
      {/* Scroll anchor */}
      <div ref={topRef} data-id="element-4821" />
      {/* Standard NavBar */}
      <div className="mb-6" data-id="element-4822">
        <NavBar tabs={[]} title="Club Builder" data-id="element-4823" />
      </div>

      {/* Page Header */}
      <div className="mb-6" data-id="element-4824">
        <button onClick={() => navigate('/club-builder')} className="flex items-center gap-1.5 text-[14px] text-[#475569] hover:text-[#0F172A] transition-colors mb-4" data-id="element-4825">
          <ArrowLeft className="w-4 h-4" data-id="element-4826" />
          Back to Club Builder
        </button>

        <div className="flex items-center justify-between" data-id="element-4827">
          <div className="flex items-center gap-3" data-id="element-4828">
            <h2 className="text-[28px] font-bold text-[#0F172A]" data-id="element-4829">
              New Wine Club
            </h2>
            <Badge variant="warning" data-id="element-4830">Draft</Badge>
          </div>

          <div className="flex items-center gap-3" data-id="element-4831">
            <Button variant="outline" iconLeft={<Eye className="w-4 h-4" data-id="element-4833" />} size="sm" disabled={!previewPublishEnabled} data-id="element-4832">
              Preview
            </Button>
            <Button variant="outline" size="sm" onClick={() => showToast('Draft saved')} data-id="element-4834">
              Save Draft
            </Button>
            <Button variant="primary" size="sm" disabled={!previewPublishEnabled} onClick={() => showToast('Wine club published!')} data-id="element-4835">
              Publish
            </Button>
          </div>
        </div>
      </div>

      {toast && <div className="fixed bottom-6 right-6 z-50 bg-[#10B981] text-white text-sm font-medium px-4 py-3 rounded-lg shadow-lg" data-id="element-4836">
          {toast}
        </div>}

      {/* Tab Navigation */}
      <nav className="bg-white border-b border-[#E2E8F0] rounded-t-lg px-6 flex items-end" data-id="element-4837">
        {tabsWithCounts.map(tab => {
        const tabIndex = TAB_INDEX[tab.id];
        const isActive = activeTab === tab.id;
        const isLocked = tabIndex > completedUpTo && !isActive;
        const isCompleted = tabIndex <= completedUpTo && !isActive;
        return <button key={tab.id} onClick={() => {
          if (!isLocked) setActiveTab(tab.id);
        }} disabled={isLocked} className={`
                relative flex items-center gap-1.5
                text-[13px] font-semibold
                px-[18px] py-3
                border-b-2 transition-colors duration-200
                focus:outline-none
                ${isActive ? 'text-[#0EA5E9] border-[#0EA5E9]' : isCompleted ? 'text-[#10B981] border-transparent hover:text-[#059669]' : 'text-[#94A3B8] border-transparent cursor-not-allowed'}
              `} data-id="element-4838">
              {isCompleted && <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#10B981] flex-shrink-0" data-id="element-4839">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-4840" />
                </span>}
              {isLocked && <Lock className="w-3.5 h-3.5 flex-shrink-0" data-id="element-4841" />}
              {tab.label}
              {'count' in tab && tab.count !== undefined && <span className={`
                    inline-flex items-center justify-center
                    text-[11px] font-semibold
                    rounded-full min-w-[20px] h-5 px-1.5
                    ${isActive ? 'bg-[#E0F2FE] text-[#0284C7]' : isCompleted ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-[#F1F5F9] text-[#94A3B8]'}
                  `} data-id="element-4842">
                  {tab.count}
                </span>}
            </button>;
      })}
      </nav>

      {/* Tab Content */}
      <div className="pt-6" data-id="element-4843">
        {activeTab === 'tab-basics' && <ClubBuilderBasicsPanel onNext={() => advanceFrom(0)} boxType={boxType} onBoxTypeChange={setBoxType} data-id="element-4844" />}

        {activeTab === 'tab-pack-sizes' && <PackSizesPanel boxType={boxType} onBack={() => setActiveTab('tab-basics')} onNext={() => {
        if (packSizes.length > 0) advanceFrom(1);
      }} packSizes={packSizes} setPackSizes={setPackSizes} data-id="element-4845" />}

        {activeTab === 'tab-membership' && <MembershipPanel onBack={() => setActiveTab('tab-pack-sizes')} onNext={() => advanceFrom(2)} packSizes={packSizes} data-id="element-4846" />}

        {activeTab === 'tab-flow' && <FlowPanel onBack={() => setActiveTab('tab-membership')} onNext={() => advanceFrom(3)} data-id="element-4847" />}

        {activeTab === 'tab-theming' && <ThemingPanel onBack={() => setActiveTab('tab-flow')} onNext={() => advanceFrom(4)} data-id="element-4848" />}

        {activeTab === 'tab-selling-plans' && <SellingPlansPanel onBack={() => setActiveTab('tab-theming')} data-id="element-4849" />}
      </div>
    </div>;
}